import React, { useState, useRef, useEffect, forwardRef, useImperativeHandle, useCallback, useLayoutEffect, useMemo } from 'react';
import { Table, Input, Select, Button, Space, DatePicker, InputNumber, Dropdown, Checkbox } from 'antd';
import { SearchOutlined } from '@ant-design/icons';
import ResizableTitle  from './ResizableTitle.jsx';
import 'react-resizable/css/styles.css';
import './Tabler.css'
import axiosInstance from './axiosInstance';
import moment from 'moment';
import FilterForm from './FilterForm.jsx';
import {OpenDialog,waitRef} from './Utils.jsx';
//import { set } from '@kitware/vtk.js/macros.js';
const { Option } = Select;


function composeRefs(...refs) {
  return node => {
    refs.forEach(ref => {
      if (!ref) return;
      if (typeof ref === 'function') {
        ref(node);
      } else {
        ref.current = node;
      }
    });
  };
}

function useColumns(propColumns,createColumns,getkey) {
	const [containerSize, setContainerSize] = useState(null);
	const [containerElement, setContainerElement] = useState(null);
	const [columnWidths, setColumnWidths] = useState({});
	
	useLayoutEffect(() => {	
		if (!containerElement) return;	
		const ro = new ResizeObserver(([entry]) => {
			const rect = entry.contentRect
			setContainerSize({width: Math.floor(rect.width), height: Math.floor(rect.height)});
		});
		ro.observe(containerElement);
		return () => ro.disconnect();
	}, [containerElement]);
    
	function updateLastWidth(incolumns) {
		let columns = [...incolumns];
		const lastCol = columns[columns.length - 1];
		
		if (lastCol.width) return columns

		const fixedCols = columns.slice(0, -1);	
			
		if (containerSize) {
			const fixedWidth = fixedCols.reduce( (sum, col) => sum + (col.width ?? 0), 0);
			const lastWidth = Math.max(containerSize.width - fixedWidth - 30, 0);
			columns = [...fixedCols,{...lastCol, width: lastWidth>0?lastWidth:1}]	
		}
		else 
			columns = [...fixedCols,{...lastCol, width: 1000}]
	
		return columns
	}
	function updateMaxWidths(incolumns){
		let columns = [...incolumns];
		if(!columns[columns.length - 1].width) columns = columns.slice(0, -1)
		
		columns.forEach( col => {	
			col.onCell = () => ({
				style: {
					maxWidth: 100,//col.width??100,
					//whiteSpace: 'nowrap',
					overflow: 'hidden',
					textOverflow: 'ellipsis',
				},
			})
		})
		return columns
	}
	function updateResizeWidths(columns){
		if(columnWidths && Object.keys(columnWidths).length>0) {
			const cc = Object.fromEntries(columns.map(col => [getkey(col.dataIndex),col]))	
			Object.entries(columnWidths).forEach( ([k,v]) => {
				if(cc[k] && cc[k].width) cc[k] = {...cc[k],width:Math.min(Math.max(v,30),1000)};
			})
			return Object.values(cc)	
		}
		return columns
	}

	const columnsState = useMemo(() => {
		let columns = [...propColumns];
		columns = createColumns(columns);
		columns = updateResizeWidths(columns);
		columns = updateLastWidth(columns);
		//columns = updateMaxWidths(columns);
		return columns
	}, [propColumns, containerSize, columnWidths]);

  	return [setContainerElement, setColumnWidths, columnsState];
}


const Tabler = forwardRef((props,ref) => {	
	const { 
		entity,
		columns,
		multipleSelection=false,
		selectedRowsChanged,
		doubleClicked,
		url,
		sortData,
		searchData,
		refresh, 
		dataSource,
		rowKey='recid',
		resizable=true,
		actionColumn,
		...restProps 
	} = props;
	
	//const [columnsState, setColumnsState] = useState(createColumns(columns))
	const [setContainerElement, setColumnWidths, columnsState] = useColumns(props.columns,createColumns,getkey);

	const [data, setData] = useState([]);
  	const [loading, setLoading] = useState(true);
  	const [pagination, setPagination] = useState({ current: 1, pageSize: 100});
	const [total, setTotal] = useState(0);			
  	const [search, setSearch] = useState([]);
	const [searchInput, setSearchInput] = useState([]);
  	const [sort, setSort] = useState([]);
	const [selectedRows, setSelectedRows] = useState([]);
	const [selectedRowKeys, setSelectedRowKeys] = useState([]);
	const dlgRef = useRef(null);
	const formRef = useRef(null);
	

	useImperativeHandle(ref, () => ({
		getSelectedRecid() { 
			return selectedRows?.length>0 ? selectedRows[0][rowKey] : 0;
		},
		getSelectedRow() { 
			return selectedRows?.length>0 ? selectedRows[0] : null;
		},
		getSelectedRows() { 
			return selectedRows; 
		},
		refresh() {
			fetchData();
		},
		setSearchInput(text) {
			if(!text && searchInput.length==0) {
				dlgRef.current.open();
				const onFinish = (searches)=>{ setSearch(searches); dlgRef.current.close(); }
				const onReset = ()=>{ setSearch([]); dlgRef.current.close(); } 
				waitRef(formRef).then(()=>{ formRef.current.open(columns,search,onFinish,onReset) })
			}
			else {
				const searchInput = text? columnsState.filter(e=>e.options?.searchable && e.options?.type=='text').map(e=>({field: getkey(e.dataIndex), operator: 'contains', value: text})) : []
				console.log('SETSEARCHINPUT',searchInput)	
				setSearchInput(searchInput);
			}
		},
		setSearch(search) {
			setSearch(search);
		}	
	}))

	function getkey(dataIndex) { return Array.isArray(dataIndex) ? dataIndex.join('.') : dataIndex }
	function getValue(record, dataIndex) {	
		if(typeof dataIndex === 'string') return record[dataIndex]
		let v = record;
		for (let k of dataIndex) if (isNull(v)) break; else v = v[k]; 
		return v;
	}
	function isNull(v) { return v === null || v === undefined || v === ''}

	function fetchDataLocal() {
		setLoading(true);
		const [offset,limit] = [(pagination.current-1)*pagination.pageSize,pagination.pageSize]
		const cc = Object.fromEntries(columnsState.map(c=>[getkey(c.dataIndex),c]))
		function mksearch(search){
			return search.map(e=>{ 
				const c = cc[e.field]; 
				return {field:e.field,filter:{operator:e.operator,value:e.value}, type:c.options?.type, dataIndex:c.dataIndex} 
			}) 	
		}
		function mksort(sort){
			return sort.map(e=>{ 
				const c = cc[e.field];
				const dir = e.direction == 'desc' ? -1 : 1;
				const sorter = localSorter(c.options?.type,c.dataIndex) 
				return (a,b)=>dir * sorter(a,b)
			}) 
		}
		
		const search0 = mksearch(searchInput)
		const search1 = mksearch(search)
		const sort1 = mksort(sort)
		
		let data = [...dataSource];
		if(search0.length) data = data.filter(r=>{return search0.filter(s=>localFilter(s.filter,r,s.dataIndex,s.type)).length > 0 })
		if(search1.length) data = data.filter(r=>{return search1.filter(s=>localFilter(s.filter,r,s.dataIndex,s.type)).length == search1.length })
		
		if(sort1.length) data.sort(sort1[0])
		
		const total = data.length;
		if(limit) data = data.slice(offset,offset+limit)	
			
		setTotal(total);
		setData(data);
		setLoading(false);
	}

	function resetSelectedRows(){
		let kk = selectedRowKeys;
		const rr = data.filter((r) => kk.includes(r[rowKey]));
		setSelectedRows(rr)
	}
	async function fetchData() {
		setLoading(true);
		if(dataSource) { fetchDataLocal(); /*resetSelectedRows();*/ return; }
		const searching = searchInput.length==0 ? { search,searchData } : { search:searchInput,searchData:[...searchData??[],...search], searchLogic:'OR' }
		try {
			const response = await axiosInstance.post(url, {
				entity,
				offset:(pagination.current-1)*pagination.pageSize,
				limit:pagination.pageSize, 
				...searching, 
				sort,sortData,
			});
			//data = response.data.map(e=>{ return {key:e.matdscId,matfrm:e?.matfrm?.code,matcnt:e?.matcnt?.code,matsta:e?.matsta?.code}})
			//var data = response.data.filter(e=>e.matfrm||e.matcnt||e.matsta)
			console.log(response.data)
			setTotal(response.data.total);
			setData(response.data.records);
		} catch (error) {
			console.error('Error fetching data:', error);
		} finally {
			setLoading(false);
		}
	};
	
	useEffect(() => {fetchData()}, [pagination, search, searchData, searchInput, sort, refresh, dataSource]);

	function handleChanges(pagination, filters, sorter) {
		//console.log('FILTERS',pagination,filters,sorter)
		setPagination(pagination);
		let ff = Object.entries(filters).filter(([k,v])=>v?.length).map(([k,v])=>({field:k,...v[0]}))
		setSearch(ff);
		let ss = sorter?.order ? [{field: getkey(sorter.field), direction: sorter.order.replace('end','')}] : []
		setSort(ss);
		//console.log('FILTERS',ff,ss)
	};
	
	function ColumnFilter({ name, type, filter, onChange, onReset }) { 
		const [operator, setOperator] = useState(filter?.operator??'is');
		const [value, setValue] = useState(filter?.value??'');	
		const handleFilter = () => { onChange({operator,value}) }
		const handleReset = () => { setOperator('is'); setValue(''); onReset() }

		const getopers = (type) => {
			return [
				['is','=',['text','number','date','boolean']],
				['lt','<',['number','date']],
				['gt','>',['number','date']],
				['le','<=',['number','date']],
				['ge','>=',['number','date']],
				['between','mezi',['number','date']],
				['contains','obsahuje',['text']],
				['begins','začíná', ['text']],
				['ends','končí',['text']],
				['is null','null',['text','number','date','boolean']],
				['is not null','not null',['text','number','date','boolean']],
			].filter(e=>e[2].includes(type)).map(e=>{return {key:e[0],value:e[1]}})
		}
		
		const operators = (type)=>{  
			return (
			<Select value={operator} onChange={value => setOperator(value)} size='small' style={{ width: 100, marginRight: '8px' }}>
				{getopers(type).map(e => <Option key={e.key} value={e.key}>{e.value}</Option>)} 
			</Select>
			)
		}
		const buttons = (
			<> 
			<Button type="primary" icon={<SearchOutlined/>} onClick={handleFilter} size="small" style={{marginRight: '8px'}}>Search</Button>
			<Button onClick={handleReset} size="small">Reset</Button>
			</>
		)
	
		return ( 
		<div>
			{operators(type)}
			{
				type=='number'?	
				<InputNumber
					id={`input${name}`} 
					placeholder="Enter number" style={{ width: 120, marginRight: '8px' }} size="small"
					value={value} onChange={value => setValue(value)}	
				/>
			: 	type=='date' && operator == 'between'?
				<DatePicker.RangePicker
					id={`input${name}`}
					placeholder="Enter date" format="DD.MM.YYYY" style={{ width: 240, marginRight: '8px' }} size="small"
					value={value} onChange={value => setValue(value)}	
				/>
			: 	type=='date'?
				<DatePicker
					id={`input${name}`}
					placeholder="Enter date" format="DD.MM.YYYY" style={{ width: 120, marginRight: '8px' }} size="small" 
					value={value} onChange={value => setValue(value)}	
				/>

			:   type=='boolean'?
				<Checkbox
					id={`input${name}`} 
					placeholder="" style={{ width: 120, marginRight: '8px' }} size="small"
					checked={!!value} onChange={e => setValue(e.target.checked)}
	  			/>

			: 	<Input
					id={`input${name}`}
					placeholder="Enter text" style={{ width: 120, marginRight: '8px' }} size="small"
					value={value} onChange={e => setValue(e.target.value)} onPressEnter={e=>handleFilter()}	
				/>
			}
			{buttons}	
		</div>
		)		
	};

	function localFilter(value, record, dataIndex, type) {
		if (isNull(value)) return true;	
		let op = value.operator;
		let v0 = value.value;
		let rv = getValue(record,dataIndex);
		
		if(op == 'is null') return isNull(rv)
		if(op == 'is not null') return !isNull(rv)
		else if (type == 'number' || type == 'date' || type == 'boolean') {
			switch (op) {
				case 'is': return rv == v0;
				case 'gt': return rv > v0;
				case 'lt': return rv < v0;
				case 'ge': return rv >= v0;
				case 'le': return rv <= v0;
				default: return false;
			}
		} 
		else
			switch (op) {
				case 'is': return (rv??'').toString().toLowerCase() == (v0.toString().toLowerCase());
				case 'contains': return (rv??'').toString().toLowerCase().includes(v0.toString().toLowerCase());
				case 'begins': return (rv??'').toString().toLowerCase().startsWith(v0.toString().toLowerCase());
				case 'ends': return (rv??'').toString().toLowerCase().endsWith(v0.toString().toLowerCase());
				default: return false;
			}
	}
	
	function localSorter(type,dataIndex){
		if(type=='number' || type == 'date' || type == 'boolean') 
			return (a, b) => { return getValue(a,dataIndex) - getValue(b,dataIndex)}
		else 
			return (a, b) => { return (getValue(a,dataIndex)??'').toLowerCase().localeCompare((getValue(b,dataIndex)??'').toLowerCase()) }
	}

	function createFilter(column){
		let opt = column.options
		if(!opt?.searchable) return {}
	
		var type = opt?.type ?? 'text'
		var dataIndex = column.dataIndex
		var name = getkey(dataIndex)
		var id = `input${name}`
		var filter = opt.filter

		return {
			filterDropdown: ({ setSelectedKeys, selectedKeys, confirm, clearFilters })=>(
				<div style={{ padding: 8 }}>
					<ColumnFilter
						name={name}
						type={type}
						filter={selectedKeys?.length ? selectedKeys[0]:null}
						onChange={ (filter) => { setSelectedKeys(filter ? [filter] : []); confirm() }}
						onReset={ ()=> { clearFilters(); confirm() } }
					/>
				</div>
			),
			filterIcon: filtered => {
				return <SearchOutlined style={{ color: filtered ? '#1890ff' : undefined, marginRight:'5px' }} />
			},
			//onFilter: (value, record) => { return localFilter(value, record, dataIndex, type) },
			onFilterDropdownOpenChange: (visible) => {
				if (visible) setTimeout(() => document.getElementById(id).select(), 1000);
			},
			defaultFilteredValue: filter?[filter]:[],
		} 
	}
	
	function createSort(column) {
		let opt = column.options;
		if(!opt?.sortable) return {}
		let type = opt?.type ?? 'text'
		let order = opt?.sort?.order
		return {
			sorter: () => 0, //localSorter(type, column.dataIndex),
			sortDirections: ['descend', 'ascend'],
			defaultSortOrder: order,
		}
	}

	function createResize(column) {
		return !resizable?{}:{
			//width: column.width??1000,
			onHeaderCell: column => ({
				width: column.width,
				onResize: handleResize(getkey(column.dataIndex))
			})
		} 
	}
		
    function handleResize(key) {
		return (e, { size }) => {
			console.log('RESIZE',key,size.width)
			/*
			setColumnsState(prevColumns => {
				return prevColumns.map(col => {
					return getkey(col.dataIndex) === key ? { ...col, width: col.width + (size.width - col.width)/1 } : col;
				})
			});
			*/
			
			setColumnWidths( prev => {
				const oo = {...prev};
				oo[key] = size.width;
				return oo;
			})
				
		};
	}  	
	

	function createColumns(columns){
		return (actionColumn?[actionColumn]:[]).concat(columns.map(c => ({
			...c, 
			...createFilter(c), 
			...createSort(c), 
			...createResize(c) 
		})))
	}

	const paginationProp = {
		defaultCurrent: 1,
		current: pagination.current, 
		pageSize: pagination.pageSize, 
		showSizeChanger: true,
		total, 
		showTotal: (total) => `Total: ${total}`,
	};
	
	const rowSelection = {
		type: !multipleSelection?'radio':'checkbox',
		columnWidth: 1,
  		renderCell: () => " ",
		selectedRowKeys,
		onChange: (selectedRowKeys, selectedRows) => {
			setSelectedRows(selectedRows);
			setSelectedRowKeys(selectedRowKeys); 
		  	if(selectedRowsChanged) selectedRowsChanged(selectedRowKeys,selectedRows) 
		},
	};
	
	const onRow = (record) => ({
    	onClick: () => {
			const key = record[rowKey];
			let kk = selectedRowKeys;
			kk = multipleSelection? (kk.includes(key) ? kk.filter((k) => k !== key) : [...kk, key]) : [key];
			const rr = data.filter((r) => kk.includes(r[rowKey]));
			rowSelection.onChange(kk,rr); 
        },
		onDoubleClick: () => { 
			if(doubleClicked) doubleClicked(record)
		},
    })


	const components = {
		header: {
		  cell: ResizableTitle,
		},
	};
    
	const createFilterForm = useCallback(() => {
		//const onFinish = (searches)=>{ setSearch(searches); dlgRef.current.close(); }
		//const onReset = ()=>{ setSearch([]); dlgRef.current.close(); }
		//return <FilterForm columns={columns} searches={search} onFinish={onFinish} onReset={onReset} ref={formRef}/>;
		return <FilterForm ref={formRef}/>;
	},[])	
	

	 return (
	 <div ref = {composeRefs(ref, setContainerElement)}>
	 <Table 
		{...props}
		columns = {columnsState} 
      	dataSource={data}
      	loading={loading}
		pagination={paginationProp}
		onChange={handleChanges}
		rowSelection = {rowSelection}
		onRow={onRow}
		className =  "tabler"
		//rowClassName={() => 'custom-row'}
		//rowKey='recid'
		components={components}
		bordered	
		scroll={{ y: 500, x: 'max-content' }}	
		sticky
		//tableLayout="fixed"
		showSorterTooltip={false}
		{...restProps}
	/>
	<OpenDialog width={560} title="Filtr" Component={useCallback(() => <FilterForm ref={formRef}/>,[])} ref={dlgRef}/>
	</div>
	);
	
	
});

export default Tabler;